import { parseSearchParams } from "@/utils/parse";
import { Button, Flex, Menu, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import type { UseFormReturnType } from "@mantine/form";
import type { DiscoverFilterValues } from "@/types/features/discover";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

export function DiscoverFilterDate({
  form,
  onSubmit,
}: {
  form: UseFormReturnType<DiscoverFilterValues>;
  onSubmit: (values: DiscoverFilterValues) => void;
}) {
  const [opened, setOpened] = useState(false);
  const [searchParams] = useSearchParams();
  const dateValue = form.values.date || { from: null, to: null };

  // Read filter values directly from search params for labels
  const queryValues = useMemo(() => parseSearchParams(searchParams), [searchParams]);
  const queryDate = queryValues.date || { from: null, to: null };

  const initialDateRef = useRef<{ from: Date | null; to: Date | null }>(
    form.values.date ? { ...form.values.date } : { from: null, to: null }
  );

  const prevOpenedRef = useRef(opened);

  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      initialDateRef.current = form.values.date
        ? { ...form.values.date }
        : { from: null, to: null };
    }
    prevOpenedRef.current = opened;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const isFormDirty = useMemo(() => {
    const initial = initialDateRef.current;
    const current = form.values.date || { from: null, to: null };

    // Compare dates
    const initialFromTime = initial.from?.getTime() ?? null;
    const initialToTime = initial.to?.getTime() ?? null;
    const currentFromTime = current.from?.getTime() ?? null;
    const currentToTime = current.to?.getTime() ?? null;

    return (
      initialFromTime !== currentFromTime || initialToTime !== currentToTime
    );
  }, [form.values.date]);

  const handleApply = () => {
    onSubmit(form.values);
    initialDateRef.current = form.values.date
      ? { ...form.values.date }
      : { from: null, to: null };
    setOpened(false);
  };

  const handleReset = () => {
    form.setFieldValue("date", undefined);
    const emptyDate = { from: null, to: null };
    initialDateRef.current = emptyDate;
    onSubmit({ ...form.values, date: undefined });
    setOpened(false);
  };

  const hasDateFilter = queryDate.from !== null || queryDate.to !== null;

  return (
    <Menu
      width={250}
      closeOnClickOutside={false}
      closeOnItemClick={false}
      position="bottom-start"
      opened={opened}
      onChange={setOpened}
    >
      <Menu.Target>
        <Button
          size="xs"
          variant="subtle"
          rightSection={
            <IconChevronDown
              color={
                hasDateFilter
                  ? "var(--mantine-color-eu_flag-9)"
                  : "var(--mantine-color-gray-9)"
              }
              size={12}
            />
          }
        >
          <Text
            fz={15}
            c={hasDateFilter ? "eu_flag.9" : "gray.9"}
            fw={hasDateFilter ? 600 : 400}
          >
            Date
          </Text>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Flex direction="column" p="xs" gap="sm">
          <DatePickerInput
            flex={1}
            label="From date"
            placeholder="Pick date"
            leftSection={<IconCalendar size={16} stroke={1.5} />}
            value={dateValue.from}
            onChange={(value) => {
              let date: Date | null = null;
              if (value === null) {
                date = null;
              } else if (typeof value === "string") {
                date = new Date(value);
              } else {
                date = value as Date;
              }
              form.setFieldValue("date", {
                ...dateValue,
                from: date,
              });
            }}
          />
          <DatePickerInput
            flex={1}
            label="To date"
            placeholder="Pick date"
            leftSection={<IconCalendar size={16} stroke={1.5} />}
            value={dateValue.to}
            onChange={(value) => {
              let date: Date | null = null;
              if (value === null) {
                date = null;
              } else if (typeof value === "string") {
                date = new Date(value);
              } else {
                date = value as Date;
              }
              form.setFieldValue("date", {
                ...dateValue,
                to: date,
              });
            }}
          />
          <Button
            fullWidth
            mt="sm"
            onClick={handleApply}
            disabled={!isFormDirty}
          >
            Apply
          </Button>
          <Button fullWidth variant="subtle" color="gray" onClick={handleReset}>
            Reset
          </Button>
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}
